import {useQuery} from '@tanstack/react-query';
import {Alert, Box, CircularProgress} from '@mui/material';
import apiCall from '../../../../utils/api';
import Indicator from '../../components/Indicator';
import Legend from '../../components/Legend';
import {IDashboardData, IFdcDashboardResult, SurveySlug} from '../../utils/types';
import {FDC_LABELS} from '../labels';

interface Props {
    container: string;
    uoaId: string;
    surveyId: string;
    surveyEvalId: string;
}

const buildApiKey = (slug: SurveySlug): string => (slug.denominator ? `${slug.numerator.question}_${slug.denominator.question}` : slug.numerator.question);

const buildSlugs = (surveyId: number, surveyEvalId: number): SurveySlug[] => [
    {
        numerator: {survey_id: surveyId, question: 'question1'},
        denominator: {survey_id: surveyId, question: 'question2'},
        operation: 'sum',
        key: 'i1',
        roman: 'I',
        title: 'Leva campagne raccolta fondi a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il valore delle donazioni ricevute in bandi a raccolta nel corso dell'anno e il valore dei contributi deliberati nei bandi a raccolta (al netto delle donazioni ricevute su tali bandi) nel corso dell'anno.",
    },
    {
        numerator: {survey_id: surveyId, question: 'question3'},
        denominator: {survey_id: surveyId, question: 'question4'},
        operation: 'sum',
        key: 'i2',
        roman: 'II',
        title: 'Donatori per progetto sostenuto a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero di donatori raggiunti tramite bandi a raccolta nell'anno e il numero di progetti sostenuti tramite bandi a raccolta nel corso dell'anno.",
    },
    {
        numerator: {survey_id: surveyId, question: 'question6'},
        denominator: {survey_id: surveyId, question: 'question7'},
        operation: 'sum',
        key: 'i3',
        roman: 'III',
        title: 'Tasso di acquisizione nuovi donatori a confronto con la media',
        tooltip: "L'indicatore mostra il rapporto fra il numero di nuovi donatori nell'anno e il numero totale di donatori nell'anno.",
    },
    {
        numerator: {survey_id: surveyId, question: 'question5'},
        denominator: {survey_id: surveyEvalId, question: 'question1'},
        operation: 'sum',
        key: 'i4',
        roman: 'IV',
        title: 'Donazioni per abitante adulto a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero complessivo di donazioni ricevute nel corso dell'anno e il numero di abitanti adulti del territorio di riferimento.",
    },
    {
        numerator: {survey_id: surveyId, question: 'question7'},
        denominator: {survey_id: surveyEvalId, question: 'question1'},
        operation: 'sum',
        key: 'i5',
        roman: 'V',
        title: 'Donatori per abitante adulto a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero totale di donatori nell'anno e il numero di abitanti adulti del territorio di riferimento.",
    },
];

const Dashboard1 = ({container, uoaId, surveyId, surveyEvalId}: Props) => {
    const slugs = buildSlugs(Number(surveyId), Number(surveyEvalId));

    const {data, isLoading, isError} = useQuery<IFdcDashboardResult>({
        queryKey: ['fdc-dashboard1', container, uoaId, surveyId, surveyEvalId],
        queryFn: async () => {
            const res = await apiCall({
                url: `containers/${container}/uoa/${uoaId}/dashboards/fsv`,
                method: 'POST',
                data: {
                    with_groups: 1,
                    question_slugs: slugs.map((s) => ({
                        numerator: s.numerator,
                        denominator: s.denominator,
                        operation: s.operation,
                    })),
                },
            });

            const groupNumber: number | null = res.data['__group_number'] ?? null;
            const indicators: IDashboardData = {};
            for (const slug of slugs) {
                const apiKey = buildApiKey(slug);
                indicators[slug.key] = res.data[apiKey] ?? {uoa: null, average: null, group: null};
            }
            return {indicators, groupNumber};
        },
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    return (
        <Box>
            <Legend labels={FDC_LABELS} groupNumber={data.groupNumber} />

            {slugs.map((slug) => (
                <Indicator
                    key={slug.key}
                    roman={slug.roman}
                    title={slug.title}
                    tooltip={slug.tooltip}
                    data={data.indicators[slug.key]}
                    isCount={true}
                    labels={FDC_LABELS}
                />
            ))}
        </Box>
    );
};

export default Dashboard1;
